import React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import './home.css'

const Home = () => {
  const history = useHistory()

  const handleGetStarted = () => {
    history.push('/login')
  }

  return (
    <div className="home-container">
      <Helmet>
        <title>Real Time Security - AI-Powered Anti-Cheating Platform</title>
        <meta name="description" content="Professional AI-powered proctoring platform for secure online examinations" />
      </Helmet>

      {/* Navbar */}
      <nav className="home-navbar">
        <div className="home-navbar-content">
          <div className="home-logo">
            <span className="home-logo-text">Real Time Security</span>
          </div>
          <div className="home-nav-links">
            <a href="#features" className="home-nav-link">Features</a>
            <a href="#how-it-works" className="home-nav-link">How It Works</a>
            <a href="#roles" className="home-nav-link">Roles</a>
            <button onClick={handleGetStarted} className="home-nav-button">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="home-hero">
        <div className="home-hero-content">
          <h1 className="home-hero-title">
            Secure Online Examinations with
            <span className="home-hero-highlight"> AI-Powered Proctoring</span>
          </h1>
          <p className="home-hero-subtitle">
            Advanced anti-cheating technology with real-time monitoring, face recognition,
            and intelligent violation detection to ensure examination integrity.
          </p>
          <div className="home-hero-buttons">
            <button onClick={handleGetStarted} className="home-button-primary">
              Get Started Free
            </button>
            <button onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                    className="home-button-secondary">
              Learn More
            </button>
          </div>
        </div>
        <div className="home-hero-visual">
          <div className="home-hero-card">
            <div className="home-hero-icon">🎯</div>
            <h3>99.9% Detection Rate</h3>
            <p>Advanced AI algorithms</p>
          </div>
          <div className="home-hero-card">
            <div className="home-hero-icon">👁️</div>
            <h3>Real-time Monitoring</h3>
            <p>Live proctoring sessions</p>
          </div>
          <div className="home-hero-card">
            <div className="home-hero-icon">🔒</div>
            <h3>Secure & Compliant</h3>
            <p>Bank-grade security</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="home-features">
        <div className="home-section-header">
          <h2 className="home-section-title">Powerful Features</h2>
          <p className="home-section-subtitle">
            Everything you need for secure online examinations
          </p>
        </div>
        <div className="home-features-grid">
          <div className="home-feature-card">
            <div className="home-feature-icon">👤</div>
            <h3>Face Recognition</h3>
            <p>AI-powered facial verification to ensure the right person is taking the test</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon">📹</div>
            <h3>Real-time Monitoring</h3>
            <p>Live video feeds with automatic suspicious activity detection</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon">🖥️</div>
            <h3>Screen Monitoring</h3>
            <p>Track tab switches, window changes, and prevent screen sharing</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon">📊</div>
            <h3>Advanced Analytics</h3>
            <p>Comprehensive reports and insights on test attempts and violations</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon">🔐</div>
            <h3>Secure Browser</h3>
            <p>Lockdown browser mode prevents unauthorized access during tests</p>
          </div>
          <div className="home-feature-card">
            <div className="home-feature-icon">📱</div>
            <h3>Multi-device Support</h3>
            <p>Works seamlessly across desktops, laptops, and tablets</p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="home-how-it-works">
        <div className="home-section-header">
          <h2 className="home-section-title">How It Works</h2>
          <p className="home-section-subtitle">
            Simple 3-step process for secure examinations
          </p>
        </div>
        <div className="home-steps">
          <div className="home-step">
            <div className="home-step-number">1</div>
            <h3>Create & Configure</h3>
            <p>Teachers create tests and set proctoring parameters including time limits, question pools, and security settings</p>
          </div>
          <div className="home-step">
            <div className="home-step-number">2</div>
            <h3>Verify & Monitor</h3>
            <p>Students verify their identity through facial recognition, then AI monitors their test session in real-time</p>
          </div>
          <div className="home-step">
            <div className="home-step-number">3</div>
            <h3>Review & Report</h3>
            <p>Invigilators review flagged attempts while the system generates comprehensive analytics and certificates</p>
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="home-roles">
        <div className="home-section-header">
          <h2 className="home-section-title">Built for Every Role</h2>
          <p className="home-section-subtitle">
            Tailored experiences for all stakeholders
          </p>
        </div>
        <div className="home-roles-grid">
          <div className="home-role-card">
            <div className="home-role-icon">🎓</div>
            <h3>Students</h3>
            <ul className="home-role-list">
              <li>Take tests securely from anywhere</li>
              <li>View test history and scores</li>
              <li>Download certificates</li>
              <li>Track performance metrics</li>
            </ul>
          </div>
          <div className="home-role-card">
            <div className="home-role-icon">👨‍🏫</div>
            <h3>Teachers</h3>
            <ul className="home-role-list">
              <li>Create and manage tests</li>
              <li>Configure proctoring settings</li>
              <li>View student performance</li>
              <li>Generate detailed reports</li>
            </ul>
          </div>
          <div className="home-role-card">
            <div className="home-role-icon">👁️</div>
            <h3>Invigilators</h3>
            <ul className="home-role-list">
              <li>Monitor live test sessions</li>
              <li>Review flagged attempts</li>
              <li>Investigate violations</li>
              <li>Real-time intervention</li>
            </ul>
          </div>
          <div className="home-role-card">
            <div className="home-role-icon">⚙️</div>
            <h3>Administrators</h3>
            <ul className="home-role-list">
              <li>Manage users and roles</li>
              <li>Oversee all tests and attempts</li>
              <li>Track payments and billing</li>
              <li>System configuration</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="home-cta">
        <div className="home-cta-content">
          <h2>Ready to Secure Your Examinations?</h2>
          <p>Join thousands of institutions using Real Time Security for secure online testing</p>
          <button onClick={handleGetStarted} className="home-cta-button">
            Start Free Trial
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="home-footer-content">
          <div className="home-footer-section">
            <h4>Real Time Security</h4>
            <p>AI-Powered Anti-Cheating Platform</p>
          </div>
          <div className="home-footer-section">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#roles">Roles</a>
          </div>
          <div className="home-footer-section">
            <h4>Company</h4>
            <a href="#about">About Us</a>
            <a href="#contact">Contact</a>
            <a href="#privacy">Privacy Policy</a>
          </div>
          <div className="home-footer-section">
            <h4>Support</h4>
            <a href="#help">Help Center</a>
            <a href="#docs">Documentation</a>
            <a href="#api">API</a>
          </div>
        </div>
        <div className="home-footer-bottom">
          <p>&copy; 2025 Real Time Security. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}

export default Home
